import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { laborInsApi, F936 } from '../../../services/laborInsApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useLaborInsForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F936[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  const [salary, setSalary] = useState(0);
  const [reserved, setReserved] = useState(0);
  const [dailyFee, setDailyFee] = useState(0);
  const [monthlyFee, setMonthlyFee] = useState(0);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await laborInsApi.list(1, 500);
      setRecords(res.data || []);
      return res.data || [];
    } catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  const syncFrom = useCallback((r: F936) => {
    setSalary(r.f936_01); setReserved(r.f936_11);
    setDailyFee(r.f936_12); setMonthlyFee(r.f936_13);
  }, []);

  useEffect(() => { if (current && mode === 'browse') syncFrom(current); }, [current, mode, syncFrom]);
  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => {
    setMode('add'); setSalary(0); setReserved(0); setDailyFee(0); setMonthlyFee(0);
  };
  const startEdit = () => { if (current) { setMode('edit'); syncFrom(current); } };
  const cancel = () => { setMode('browse'); if (current) syncFrom(current); };

  const save = async () => {
    try {
      if (mode === 'add') {
        await laborInsApi.create({ f936_01: salary, f936_11: reserved, f936_12: dailyFee, f936_13: monthlyFee });
        message.success('新增成功');
      } else {
        await laborInsApi.update(current!.f936_01, { f936_11: reserved, f936_12: dailyFee, f936_13: monthlyFee });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetSalary = mode === 'add' ? salary : current!.f936_01;
      const idx = data.findIndex((r: F936) => r.f936_01 === targetSalary);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await laborInsApi.delete(current.f936_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1));
      else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const num = parseFloat(t);
    if (isNaN(num)) return;
    const idx = records.findIndex(r => r.f936_01 >= num);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading, records, current, currentIdx,
    salary, setSalary, reserved, setReserved, dailyFee, setDailyFee, monthlyFee, setMonthlyFee,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, quickSearch,
  };
}
